import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserInfo } from '../../features/user/userSlice'; // adjust path to your actual userSlice location
import {
  Heart,
  Baby,
  FlaskConical,
  Egg,
  Users,
  Stethoscope,
  Check,
  Loader2,
  Dna,
} from 'lucide-react';
import config from '../../config'; // adjust relative path to your project's config.js
import { FAMILY_PLANNING_GOALS, goalHasFeature } from '../../constants/familyPlanningGoals';

// ---------------------------------------------------------------------------
// API — uses the same token source as the rest of the app's onboarding
// routes (Redux state.user.token via OnboardingRoute in App.js).
// ---------------------------------------------------------------------------

async function apiRequest(path, token, options = {}) {
  const res = await fetch(`${config.baseURL}/api/onboarding${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body.data;
}

// ---------------------------------------------------------------------------
// Goal options. Values and labels come from constants/familyPlanningGoals.js
// (the same list the profile dropdown, dashboard and Health page use), so
// they can never drift apart. This page only adds the look: description,
// icon and colour for each goal.
// ---------------------------------------------------------------------------

const GOAL_DISPLAY = {
  planning_pregnancy: {
    desc: "We're thinking about starting or growing our family soon.",
    icon: Heart,
    color: '#D85A30',
  },
  currently_pregnant: {
    desc: "I'm pregnant now and want to track this pregnancy.",
    icon: Baby,
    color: '#0F6E56',
  },
  ivf_journey: {
    desc: "I'm going through, or considering, IVF treatment.",
    icon: FlaskConical,
    color: '#185FA5',
  },
  egg_donation: {
    desc: "I'm interested in donating or receiving donor eggs.",
    icon: Egg,
    color: '#B4780A',
  },
  surrogacy: {
    desc: "I'm exploring or currently part of a surrogacy arrangement.",
    icon: Users,
    color: '#8B5CF6',
  },
  fertility_consultation: {
    desc: 'I want a general consultation about my fertility health.',
    icon: Stethoscope,
    color: '#B3261E',
  },
};

const GOALS = FAMILY_PLANNING_GOALS.map((g) => ({
  id: g.value,
  label: g.label,
  desc: GOAL_DISPLAY[g.value]?.desc || '',
  icon: GOAL_DISPLAY[g.value]?.icon || Heart,
  color: GOAL_DISPLAY[g.value]?.color || '#0F4C75',
  // Small tag so the user can see what their choice unlocks
  includesCoupleDna: goalHasFeature(g.value, 'couple_dna'),
}));

const FamilyPlanningGoal = () => {
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // FIXED: previously fetched /status with no guard against (a) React 18
  // Strict Mode double-invoking this effect in dev — which fired the same
  // request twice on every mount, matching the duplicated network entries
  // — and (b) the component unmounting before the fetch resolved, which
  // would call setState on an unmounted component. `ignore` short-circuits
  // the .then()/.catch()/.finally() once cleanup has run, and the
  // AbortController cancels the in-flight request outright on unmount
  // instead of letting a stale response quietly complete later.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let ignore = false;
    const controller = new AbortController();

    apiRequest('/status', token, { signal: controller.signal })
      .then((data) => {
        if (ignore) return;
        if (data?.family_planning_goal) setSelected(data.family_planning_goal);
      })
      .catch((err) => {
        if (ignore || err.name === 'AbortError') return;
        setError(err.message);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [token]);

  async function handleFinish() {
    if (!selected) {
      setError('Choose the option that best describes your journey.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiRequest('/goal', token, { method: 'PUT', body: JSON.stringify({ goal: selected }) });
      await apiRequest('/complete', token, { method: 'POST' });

      // Keep Redux in step with the DB:
      // - onboarding_completed: App.js's DashboardRoute checks it; without
      //   this the user is sent straight back to this page.
      // - family_planning_goal: the profile, dashboard checklist and the
      //   Health page's Couple DNA tab all read it from Redux.
      dispatch(updateUserInfo({ onboarding_completed: 1, family_planning_goal: selected }));

      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not save your selection');
    } finally {
      setSaving(false);
    }
  }

  // FIXED: <style>{STYLES}</style> used to be duplicated — once inside the
  // `loading` branch's JSX, once inside the main branch's JSX. Flipping
  // `loading` from true to false tore down the whole loading subtree
  // (including its own <style> node) and mounted a brand-new subtree
  // (including a second <style> node) in the same render. That kind of
  // node swap is exactly what trips React's "Failed to execute removeChild"
  // crash — especially if a browser extension (password manager, Grammarly,
  // a translator) has also touched the DOM in between. Rendering <style>
  // once, outside the conditional, means it's never torn down and rebuilt
  // when the page finishes loading.
  return (
    <div className="fpg-page">
      <style>{STYLES}</style>

      {loading ? (
        <div className="fpg-loading">
          <Loader2 size={22} className="fpg-spin" />
        </div>
      ) : (
        <div className="fpg-card">
          <div className="fpg-step-icon">
            <Heart size={22} />
          </div>
          <h2 className="fpg-title">What brings you here?</h2>
          <p className="fpg-subtitle">
            This decides which features and steps we show you. You can change it later in your profile.
          </p>

          <div className="fpg-goal-grid">
            {GOALS.map((goal) => {
              const Icon = goal.icon;
              const active = selected === goal.id;
              return (
                <button
                  key={goal.id}
                  type="button"
                  className={`fpg-goal-card ${active ? 'active' : ''}`}
                  onClick={() => {
                    setSelected(goal.id);
                    setError(null);
                  }}
                  style={active ? { borderColor: goal.color, backgroundColor: `${goal.color}0D` } : undefined}
                >
                  <span className="fpg-goal-icon" style={{ backgroundColor: `${goal.color}1A`, color: goal.color }}>
                    <Icon size={18} />
                  </span>
                  <div>
                    <p className="fpg-goal-label">{goal.label}</p>
                    <p className="fpg-goal-desc">{goal.desc}</p>
                    {goal.includesCoupleDna && (
                      <span className="fpg-goal-tag">
                        <Dna size={11} /> Includes couple DNA &amp; birth risk analysis
                      </span>
                    )}
                  </div>
                  {active && <Check size={16} color={goal.color} style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>

          {error && <p className="fpg-error">{error}</p>}

          <button className="fpg-btn-primary" onClick={handleFinish} disabled={saving}>
            {saving ? <Loader2 size={15} className="fpg-spin" /> : null}
            {saving ? 'Saving…' : 'Continue to my dashboard'}
          </button>
        </div>
      )}
    </div>
  );
};

const STYLES = `
  .fpg-page { min-height: 100vh; background: #F7F8FA; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .fpg-loading { display: flex; align-items: center; justify-content: center; color: #888; }
  .fpg-spin { animation: fpg-spin 1s linear infinite; }
  @keyframes fpg-spin { to { transform: rotate(360deg); } }
  .fpg-card { background: white; border-radius: 16px; padding: 32px; max-width: 480px; width: 100%; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
  .fpg-step-icon { width: 44px; height: 44px; border-radius: 12px; background: #0F4C7514; color: #0F4C75; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
  .fpg-title { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
  .fpg-subtitle { margin: 0 0 22px; font-size: 13.5px; color: #888; }
  .fpg-goal-grid { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
  .fpg-goal-card { display: flex; align-items: center; gap: 12px; text-align: left; padding: 14px; border-radius: 12px; border: 1px solid #E5E7EB; background: white; cursor: pointer; }
  .fpg-goal-icon { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .fpg-goal-label { margin: 0; font-size: 14px; font-weight: 700; }
  .fpg-goal-desc { margin: 2px 0 0; font-size: 12px; color: #888; }
  .fpg-goal-tag { display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; padding: 2px 8px; border-radius: 999px; background: #0F4C750F; color: #0F4C75; font-size: 11px; font-weight: 600; }
  .fpg-error { color: #B3261E; font-size: 12.5px; margin: 0 0 14px; }
  .fpg-btn-primary { width: 100%; padding: 12px; border-radius: 10px; border: none; background: #0F4C75; color: white; font-size: 14.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
  .fpg-btn-primary:disabled { opacity: 0.7; cursor: default; }
`;

export default FamilyPlanningGoal;